import { useEffect, useState } from "react";
import { Loader2, Plus, Upload, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const inputCls =
  "w-full bg-white border border-[#E8E6E1] rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C50]/30 focus:border-[#2E5C50]";

export default function CredentialUploadDialog({ open, onClose, employees, defaultEmployeeId, onUploaded }) {
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({
    caregiver_id: defaultEmployeeId || "",
    credential_type: "hha_certification",
    custom_type_label: "",
    issuing_authority: "",
    issue_date: "",
    expiration_date: "",
    notes: "",
    is_required: true,
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm((f) => ({ ...f, caregiver_id: defaultEmployeeId || f.caregiver_id }));
    api.get("/credentials/types").then((r) => setTypes(r.data.types || [])).catch(() => {});
  }, [open, defaultEmployeeId]);

  if (!open) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  async function submit(e) {
    e.preventDefault();
    if (!file) {
      toast.error("Please attach the credential document.");
      return;
    }
    if (!form.caregiver_id) {
      toast.error("Please select an employee.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v == null ? "" : String(v)));
      fd.append("file", file);
      await api.post("/credentials/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Credential uploaded.");
      onUploaded?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-testid="credential-upload-dialog" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl my-8">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="font-heading text-xl text-[#1C211F]">Upload credential</h2>
            <p className="text-xs text-[#57605A] mt-0.5">Track an employee's certification, license, or compliance document.</p>
          </div>
          <button data-testid="cud-close" onClick={onClose} className="text-[#57605A] hover:text-[#1C211F]" aria-label="Close">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.12em] font-semibold text-[#57605A]">Employee</label>
            <select data-testid="cud-employee" required value={form.caregiver_id} onChange={set("caregiver_id")} className={`${inputCls} mt-1.5`}>
              <option value="">— Select employee —</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-[0.12em] font-semibold text-[#57605A]">Credential type</label>
              <select data-testid="cud-type" required value={form.credential_type} onChange={set("credential_type")} className={`${inputCls} mt-1.5`}>
                {types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.12em] font-semibold text-[#57605A]">Issuing authority</label>
              <input data-testid="cud-authority" type="text" placeholder="e.g. Florida DOH" value={form.issuing_authority} onChange={set("issuing_authority")} className={`${inputCls} mt-1.5`} />
            </div>
          </div>

          {form.credential_type === "custom" && (
            <div>
              <label className="text-xs uppercase tracking-[0.12em] font-semibold text-[#57605A]">Custom credential name</label>
              <input data-testid="cud-custom-label" type="text" required placeholder="e.g. Dementia Care Training" value={form.custom_type_label} onChange={set("custom_type_label")} className={`${inputCls} mt-1.5`} />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-[0.12em] font-semibold text-[#57605A]">Issue date</label>
              <input data-testid="cud-issue-date" type="date" value={form.issue_date} onChange={set("issue_date")} className={`${inputCls} mt-1.5`} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.12em] font-semibold text-[#57605A]">Expiration date</label>
              <input data-testid="cud-expiration-date" type="date" value={form.expiration_date} onChange={set("expiration_date")} className={`${inputCls} mt-1.5`} />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.12em] font-semibold text-[#57605A]">Notes</label>
            <textarea data-testid="cud-notes" rows={2} value={form.notes} onChange={set("notes")} placeholder="Optional — internal notes" className={`${inputCls} mt-1.5 resize-none`} />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input data-testid="cud-required" type="checkbox" checked={form.is_required} onChange={set("is_required")} className="h-4 w-4 rounded border-[#E8E6E1] text-[#2E5C50] focus:ring-[#2E5C50]" />
            <span className="text-sm text-[#1C211F]">Required for compliance score</span>
          </label>

          <div>
            <label className="text-xs uppercase tracking-[0.12em] font-semibold text-[#57605A]">Document file</label>
            <div className="mt-1.5 border-2 border-dashed border-[#E8E6E1] rounded-md p-4 hover:border-[#2E5C50]/50 transition-colors">
              <input data-testid="cud-file" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic" required onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm w-full" />
              {file && (
                <p className="mt-2 text-xs text-[#2E5C50] inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} /> {file.name} · {(file.size / 1024).toFixed(0)} KB
                </p>
              )}
              <p className="mt-1.5 text-xs text-[#57605A]">PDF, PNG, JPG, WebP or HEIC — max 15 MB</p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-[#57605A] hover:text-[#1C211F]" data-testid="cud-cancel">Cancel</button>
            <button data-testid="cud-submit" type="submit" disabled={submitting}
              className="inline-flex items-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] rounded-md px-5 py-2.5 text-sm font-medium disabled:opacity-60">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={1.5} />}
              {submitting ? "Uploading…" : "Upload credential"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
